import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    // Only probe /auth/me if we have a saved token or auth cookie. This avoids
    // a noisy 401 in the browser console for every anonymous public-page load.
    const hasToken = !!localStorage.getItem("antera_token");
    const hasAuthCookie = document.cookie.split("; ").some((c) => c.startsWith("antera_auth="));
    if (!hasToken && !hasAuthCookie) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Persist Bearer token as backup (Safari sometimes blocks 3rd-party cookies in preview)
    if (data.access_token) {
      localStorage.setItem("antera_token", data.access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
    }
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (_) {
      // ignore - logout is best-effort
    }
    localStorage.removeItem("antera_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(false);
  }, []);

  // On mount, restore Bearer fallback from localStorage
  useEffect(() => {
    const t = localStorage.getItem("antera_token");
    if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
